import { useGetSavedSlidesQuery } from "@api/slides-library";
import {
  useSlidesLibraryStore,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { useEffect, useMemo } from "react";
import type { TSavedSlide, TSlidesFolder } from "@/shared/types";
import {
  useGetArchivedSlidesFoldersQuery,
  useGetCurrentUserQuery,
} from "@/shared/services";

const ROOT_FOLDER_ID = "recently-deleted";

const collectSubtreeIds = (
  rootId: string,
  folders: TSlidesFolder[],
): Set<string> => {
  const childrenByParent = new Map<string, TSlidesFolder[]>();
  for (const folder of folders) {
    const parent = folder.parentId ?? "";
    if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
    childrenByParent.get(parent)!.push(folder);
  }
  const ids = new Set<string>([rootId]);
  const queue: string[] = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    for (const child of childrenByParent.get(id) ?? []) {
      ids.add(child._id);
      queue.push(child._id);
    }
  }
  return ids;
};

export const useGetArchivedSlides = () => {
  const { data: me } = useGetCurrentUserQuery();
  const activeFolderId = useSlidesLibraryStore((s) => s.folderId);
  const { search, setIsSearching } = useSlidesLibraryStoreShallow((s) => ({
    search: s.search,
    setIsSearching: s.setIsSearching,
  }));

  const trimmed = search.trim();

  const browseFolderId =
    activeFolderId === ROOT_FOLDER_ID ? "" : activeFolderId;

  const { data, isLoading, isFetching } = useGetSavedSlidesQuery(
    {
      userId: me?.oktaId as string,
      folderId: trimmed ? "" : browseFolderId,
      archived: true,
    },
    {
      skip: !me?.oktaId,
    },
  );

  const { data: folders } = useGetArchivedSlidesFoldersQuery({
    enabled: !!trimmed && activeFolderId !== ROOT_FOLDER_ID,
  });

  useEffect(() => {
    if (!search) return;
    setIsSearching(isFetching);
  }, [isFetching, search, setIsSearching]);

  const allowedFolderIds = useMemo<Set<string> | null>(() => {
    if (!trimmed) return null;
    if (!activeFolderId || activeFolderId === ROOT_FOLDER_ID) return null;
    if (!folders) return new Set<string>([activeFolderId]);
    return collectSubtreeIds(activeFolderId, folders as TSlidesFolder[]);
  }, [trimmed, activeFolderId, folders]);

  const filtered = useMemo<TSavedSlide[] | undefined>(() => {
    if (!data) return data;
    if (!trimmed) return data as TSavedSlide[];
    const query = trimmed.toLowerCase();
    return (data as TSavedSlide[]).filter((slide) => {
      if (
        allowedFolderIds &&
        !allowedFolderIds.has(slide.folderId ?? "")
      )
        return false;
      return slide.title.toLowerCase().includes(query);
    });
  }, [data, trimmed, allowedFolderIds]);

  return { data: filtered, isLoading, isFetching };
};
